import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";

import { FilterBar, type FilterSpec } from "@/components/data";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { defaultGrainForRange, isGrainValue, isRangeValue, parseRange, TIME_RANGE_OPTIONS, type RangeValue } from "@/lib/time-range";

import { getCatalog, type TtuCatalog, type TtuScope } from "./api";
import { ChannelPicker } from "./components/ChannelPicker";
import { ProductPicker } from "./components/ProductPicker";
import { ScopeNote } from "./components/ScopeNote";
import { sourceCaption, SourcePicker } from "./components/SourcePicker";
import { channelLabel, type Grain } from "./format";

export type Filter = {
  region: "overview" | "uk" | "eu" | "mena";
  product: string | null;
  channel: string[];
  source: string | null;
  range: RangeValue;
  period: Grain;
};

const REGION_FILTER: FilterSpec = {
  name: "region",
  label: "Region",
  kind: "choice",
  options: [
    { value: "overview", label: "Overview" },
    { value: "uk", label: "UK" },
    { value: "eu", label: "EU" },
    { value: "mena", label: "MENA" },
  ],
};

const RANGE: FilterSpec = {
  name: "range",
  label: "Range",
  kind: "choice",
  options: TIME_RANGE_OPTIONS.map(({ value, label }) => ({ value, label })),
};

const PERIOD: FilterSpec = {
  name: "period",
  label: "Period",
  kind: "choice",
  options: [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
  ],
};

export const DEFAULT_PERIOD: Grain = "monthly";

const scalar = (value: unknown): string | null => {
  if (Array.isArray(value)) return value.length ? String(value.at(-1)) : null;
  if (typeof value === "number") return String(value);
  return typeof value === "string" && value !== "" ? value : null;
};

const list = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(String).filter(Boolean) : (typeof value === "string" || typeof value === "number") && String(value) ? [String(value)] : [];

export function narrowChannels(
  value: string[],
  region: Filter["region"],
  marketplaces: TtuCatalog["marketplaces"],
): string[] {
  if (region === "overview") return value;
  const visible = marketplaces.filter((row) => row.region === region);
  const visibleLeaves = new Set(visible.map((row) => `${row.channel}:${row.marketplace}`));
  const visibleGroups = new Set<string>(visible.map((row) => row.channel));
  const narrowed = value.filter((key) => key.includes(":") ? visibleLeaves.has(key) : visibleGroups.has(key));
  return narrowed.length > 0 ? narrowed : value;
}

function isUnfilteredUnitsTab(tab: string | undefined, filter: Pick<Filter, "product" | "channel" | "source">): boolean {
  return (tab === "units" || tab === "overview" || tab === "uk" || tab === "eu" || tab === "mena")
    && !filter.product
    && filter.channel.length === 0
    && !filter.source;
}

export function filterFromSearch(search: Record<string, unknown>, tab?: string): Filter {
  const product = scalar(search.f_product);
  const channel = list(search.f_channel);
  const source = scalar(search.f_source);
  const unfilteredUnits = isUnfilteredUnitsTab(tab, { product, channel, source });
  const rawRange = scalar(search.f_range);
  const range = isRangeValue(rawRange) ? rawRange : unfilteredUnits ? "90" : parseRange(rawRange);
  const rawRegion = scalar(search.f_region);
  const rawPeriod = scalar(search.f_period);
  return {
    region: rawRegion === "uk" || rawRegion === "eu" || rawRegion === "mena" ? rawRegion : "overview",
    product,
    channel,
    source,
    range,
    period: isGrainValue(rawPeriod) ? rawPeriod : unfilteredUnits ? defaultGrainForRange(range) : DEFAULT_PERIOD,
  };
}

export function queryFor(filter: Filter): string {
  const query = new URLSearchParams();
  query.set("region", filter.region);
  if (filter.product) query.set("product", filter.product);
  for (const channel of filter.channel) query.append("marketplace", channel);
  if (filter.source) query.set("source", filter.source);
  query.set("range", filter.range);
  query.set("grain", filter.period);
  return query.toString();
}

export function searchFor(filter: Filter, tab?: string): Record<string, string | string[]> {
  const search: Record<string, string | string[]> = { f_range: filter.range };
  if (filter.region !== "overview") search.f_region = filter.region;
  const defaultPeriod = isUnfilteredUnitsTab(tab, filter) ? defaultGrainForRange(filter.range) : DEFAULT_PERIOD;
  if (filter.period !== defaultPeriod) search.f_period = filter.period;
  if (filter.product) search.f_product = filter.product;
  if (filter.channel.length) search.f_channel = filter.channel;
  if (filter.source) search.f_source = filter.source;
  return search;
}

export type Controls = {
  range: RangeValue;
  grain: Grain;
  values: Record<string, string | string[] | null>;
  filter: Filter;
  channels: string[];
  droppedChannels: string[];
  onChange: (next: Record<string, string | string[] | null>) => void;
  setFilter: (next: Filter) => void;
  toQuery: () => string;
};

export function filterForChange(
  filter: Filter,
  next: Record<string, string | string[] | null>,
): Filter {
  const range = parseRange((next.range as string | null) ?? filter.range);
  const period = isGrainValue(next.period) ? next.period : filter.period;
  return {
    ...filter,
    region: (next.region as Filter["region"] | null) ?? filter.region,
    range,
    period,
    product: next.product === undefined ? filter.product : (next.product as string | null),
    channel: next.channel === undefined ? filter.channel : list(next.channel),
    source: next.source === undefined ? filter.source : (next.source as string | null),
  };
}

export function useControls(): Controls {
  const raw = useSearch({ strict: false }) as Record<string, unknown>;
  const params = useParams({ strict: false }) as { tab?: string };
  const tab = params.tab;
  const navigate = useNavigate();
  const filter = filterFromSearch(raw, tab);
  const catalog = useQuery({
    queryKey: ["meteoric_units_sold", "catalog", filter.range],
    queryFn: () => getCatalog(filter.range),
  });
  const channels = catalog.data
    ? narrowChannels(filter.channel, filter.region, catalog.data.data.marketplaces)
    : filter.channel;
  const visible = new Set(channels);
  const droppedChannels = filter.channel.filter((channel) => !visible.has(channel));
  const setFilter = (next: Filter) => {
    void navigate({ to: ".", search: searchFor(next, tab), replace: true });
  };
  return {
    range: filter.range,
    grain: filter.period,
    values: searchFor(filter, tab),
    filter,
    channels,
    droppedChannels,
    setFilter,
    onChange: (next) => setFilter(filterForChange(filter, next)),
    toQuery: () => queryFor(filter),
  };
}

export function ModuleControls({ controls }: { controls: Controls }) {
  const catalog = useQuery({
    queryKey: ["meteoric_units_sold", "catalog", controls.range],
    queryFn: () => getCatalog(controls.range),
  });
  const update = (patch: Partial<Filter>) => controls.setFilter({ ...controls.filter, ...patch });
  return (
    <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)] gap-3 lg:w-auto">
      <div className="flex min-w-0 flex-wrap items-end gap-4" aria-label="Module controls">
        <FilterBar
          layout="segmented"
          filters={[REGION_FILTER]}
          values={{ region: controls.filter.region }}
          onChange={(next) => update({ region: next.region as Filter["region"] })}
        />
        <ProductPicker
          products={catalog.data?.data.products ?? []}
          value={controls.filter.product}
          disabled={catalog.isPending || catalog.isError}
          onChange={(product) => update({ product })}
        />
        <ChannelPicker
          marketplaces={catalog.data?.data.marketplaces ?? []}
          region={controls.filter.region}
          value={controls.channels}
          disabled={catalog.isPending || catalog.isError}
          onChange={(channel) => update({ channel })}
        />
        <SourcePicker
          sources={catalog.data?.data.sources ?? []}
          value={controls.filter.source}
          disabled={catalog.isPending || catalog.isError}
          onChange={(source) => update({ source })}
        />
        <div className="w-full min-w-0 max-w-full overflow-x-auto lg:w-auto">
          <div className="w-max">
            <FilterBar
              layout="segmented"
              filters={[RANGE]}
              values={{ range: controls.range }}
              onChange={controls.onChange}
            />
          </div>
        </div>
        <FilterBar
          layout="segmented"
          filters={[PERIOD]}
          values={{ period: controls.grain }}
          onChange={controls.onChange}
        />
      </div>
      {controls.droppedChannels.length > 0 && <ScopeNote>{`Not in this region, so left out: ${controls.droppedChannels.map(channelLabel).join(", ")}.`}</ScopeNote>}
      {catalog.isError && <Alert><AlertDescription>Product, channel and source lists could not be read, so those filters cannot be changed right now.</AlertDescription></Alert>}
    </div>
  );
}

export function sourceScopeNote(filter: Filter, excludedChannel: string | null): string | null {
  if (!filter.source || !excludedChannel) return null;
  const kind = excludedChannel === "Amazon" ? "storefront" : "Amazon";
  const article = kind === "Amazon" ? "an" : "a";
  return `Source ${sourceCaption(filter.source)} is ${article} ${kind} source, so ${excludedChannel} marketplaces are left out of this view.`;
}

export function regionConflictNote(filter: Filter, scope: TtuScope): string | null {
  if (
    filter.channel.length === 0
    || filter.region === "overview"
    || scope.marketplaces.length > 0
    || scope.excluded_channel !== null
  ) return null;
  const channels = filter.channel.map(channelLabel).join(", ");
  const verb = filter.channel.length > 1 ? "are" : "is";
  return `${channels} ${verb} outside ${filter.region.toUpperCase()}, so this view has no data. Change the region or the channel.`;
}
